import { useState, useEffect } from 'react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogBody,
} from '../ui/Dialog';
import { Badge } from '../ui/Badge';
import { Skeleton } from '../ui/Skeleton';
import { apiClient } from '../../lib/api-client';

export interface ProductWorkflowHistoryProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  product: any;
}

function formatDate(iso?: string): string {
  if (!iso) return '—';
  const d = new Date(iso);
  return Number.isFinite(d.getTime()) ? d.toLocaleString() : iso;
}

function statusColor(status: string): string {
  switch (String(status).toLowerCase()) {
    case 'succeeded':
      return 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400';
    case 'running':
    case 'pending':
      return 'bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-400';
    case 'failed':
    case 'error':
      return 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-400';
    case 'cancelled':
      return 'bg-gray-100 text-gray-600 dark:bg-gray-900/30 dark:text-gray-500';
    default:
      return 'bg-gray-100 text-gray-800 dark:bg-gray-900/30 dark:text-gray-400';
  }
}

export function ProductWorkflowHistory({
  open,
  onOpenChange,
  product,
}: ProductWorkflowHistoryProps) {
  const productId = product?.id;
  const [runs, setRuns] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!open || !productId) return;
    setLoading(true);
    apiClient
      .getCicdProductRuns(productId)
      .then((res) => setRuns(res?.runs || []))
      .catch(() => setRuns([]))
      .finally(() => setLoading(false));
  }, [open, productId]);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent maxWidth="2xl" className="max-h-[90vh] flex flex-col">
        <DialogHeader>
          <DialogTitle>Workflow History: {productId}</DialogTitle>
        </DialogHeader>
        <DialogBody className="overflow-y-auto">
          {loading ? (
            <div className="space-y-2">
              {[1, 2, 3, 4, 5].map((i) => (
                <Skeleton key={i} className="h-12 w-full" />
              ))}
            </div>
          ) : runs.length === 0 ? (
            <p className="text-center py-8 text-gray-500 dark:text-gray-400">
              No workflow runs yet.
            </p>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-gray-200 dark:border-gray-700">
                    <th className="text-left py-2 px-3 font-medium text-gray-700 dark:text-gray-300">
                      Workflow
                    </th>
                    <th className="text-left py-2 px-3 font-medium text-gray-700 dark:text-gray-300">
                      Status
                    </th>
                    <th className="text-left py-2 px-3 font-medium text-gray-700 dark:text-gray-300">
                      Started
                    </th>
                    <th className="text-left py-2 px-3 font-medium text-gray-700 dark:text-gray-300">
                      Finished
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {runs.map((run) => (
                    <tr
                      key={run.workflowName}
                      className="border-b border-gray-100 dark:border-gray-800 hover:bg-gray-50 dark:hover:bg-gray-800/50"
                    >
                      <td className="py-2 px-3 text-gray-900 dark:text-gray-100 font-mono text-xs">
                        {run.workflowName}
                      </td>
                      <td className="py-2 px-3">
                        <Badge
                          variant="outline"
                          className={statusColor(run.status)}
                        >
                          {run.status || 'Unknown'}
                        </Badge>
                      </td>
                      <td className="py-2 px-3 text-gray-600 dark:text-gray-400">
                        {formatDate(run.startedAt)}
                      </td>
                      <td className="py-2 px-3 text-gray-600 dark:text-gray-400">
                        {formatDate(run.finishedAt)}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </DialogBody>
      </DialogContent>
    </Dialog>
  );
}
